// ── OpportunityForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Btn, FInput, FInputNum, FSelect, Field, Modal, PageLink, ReadOnlyForm, TabBtn } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { NotesPanel } from "../../../shared/NotesPanel";

const opt = (arr) => arr.map(v => ({ value: v, label: v }));
const COLLATERAL_TYPE = [{ value: "", label: "— None —" }, ...opt(["ABL", "CF", "Hybrid"])];
const FACILITY_TYPE = [{ value: "", label: "— None —" }, ...opt(["RLOC", "DDTL", "Term Loan"])];
const OPP_TYPE = [{ value: "", label: "— None —" }, ...opt(["New Business", "Existing Business"])];
const LEAD_SOURCE = [{ value: "", label: "— None —" }, ...opt([
  "Bank", "Castlelake", "Direct", "Family Office", "Investment Bank", "Law Firm / Accounting",
  "Loan Broker", "Non-Bank Lender", "Other", "Private Equity", "Turnaround Consultant",
])];
const TERMINATION_REASON = [{ value: "", label: "— None —" }, ...opt([
  "Lost - Bank", "Lost - CastleLake Denial", "Lost - Other Lender", "Lost - Price", "Lost - Structure",
  "Passed - Collateral", "Passed - Financial Issue", "Passed - Industry", "Passed - Management Team",
  "Passed - Price", "Passed - Size", "Passed - Structure", "Passed - Pre-Capital",
])];

export const OpportunityForm = ({ initial, accountid, readOnly }) => {
  const { api, role, currentUser, modal, setModal, closeModal, saving, error, saveRec } = useApp();
  const blank = {
    accountid: accountid || "", name: "", stage: "Identified", owner: "",
    opportunity_type: "", follow_up: 0, lead_source: "", source_firm: "",
    referralsourceid: "",
    rloc_amount: "", term_amount: "", initial_funding_amount: "", expected_equity_requirement: "",
    probability: "", facility_type: "", collateraltype: "",
    expected_close_date: "", term_sheet_issued_date: "", term_sheet_signed_date: "",
    hum_date: "", irc_date: "", terminated_date: "", termination_reason: "",
    next_step: "", description: "",
  };
  const [f, set, setF] = useForm(initial || blank);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [refSources, setRefSources] = useState([]);
  const [formTab, setFormTab] = useState("details");

  useEffect(() => {
    api.get("getOpportunityStages").then(r => setStages(Array.isArray(r) ? r : [])).catch(() => {});
    api.get("getUsers").then(r => setUsers(Array.isArray(r) ? r : [])).catch(() => {});
    api.get("getReferralSources").then(r => setRefSources(Array.isArray(r) ? r : [])).catch(() => {});
    if (!accountid && !initial?.accountid) api.get("getAccounts").then(r => setAccounts(Array.isArray(r) ? r : [])).catch(() => {});
    // Owner defaults to the creating user (server enforces this too).
    if (!initial?.id && currentUser?.id) set("owner")(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stageOpts = stages.map(s => ({ value: s.stage, label: s.stage }));
  const userOpts = [{ value: "", label: "— Select —" }, ...users.map(u => ({ value: u.id, label: u.FullName }))];
  // The dropdown lists ACTIVE users only. But a legacy opp may be owned by an
  // inactive user — keep that assignee visible (by name, from the record's join)
  // instead of blanking to "— Select —". Non-destructive: it's just an extra option.
  const withCurrent = (options, val, name) => {
    if (!val || options.some(o => String(o.value) === String(val))) return options;
    return [...options, { value: val, label: (name ? name + " (inactive)" : String(val)) }];
  };
  const acctOpts = [{ value: "", label: "— Select —" }, ...accounts.map(a => ({ value: a.id, label: a.companyname }))];
  // "Company — Person": several sources can share a firm, so the company alone
  // isn't enough to pick the right one. Sorted by that label.
  const refLabel = r => {
    const person = (r.full_name || [r.firstname, r.lastname].filter(Boolean).join(" ")).trim();
    const co = (r.company_name || "").trim();
    return co && person ? `${co} — ${person}` : (co || person || "(unnamed source)");
  };
  const refOpts = [{ value: "", label: "— None —" },
    ...refSources.map(r => ({ value: r.id, label: refLabel(r) })).sort((a, b) => a.label.localeCompare(b.label))];
  const lockedAccount = accountid || initial?.accountid;

  const commitment = (parseFloat(f.rloc_amount || 0) || 0) + (parseFloat(f.term_amount || 0) || 0);
  const d = v => (v ? String(v).slice(0, 10) : "");

  const missing = [];
  if (!(f.accountid || accountid)) missing.push("Account");
  if (!f.name) missing.push("Opportunity Name");
  if (!f.stage) missing.push("Stage");
  if (!f.owner) missing.push("Opportunity Owner");
  if (!String(f.lead_source ?? "").trim()) missing.push("Lead Source");
  if (!String(f.source_firm ?? "").trim()) missing.push("Source Firm");
  if (!f.expected_close_date) missing.push("Expected Close Date");
  if (f.stage === "Declined (Lost)" && !f.termination_reason) missing.push("Termination Reason");
  // A won stage needs both term-sheet dates (the API refuses it otherwise).
  const isWon = !!(stages.find(s => s.stage === f.stage) || {}).is_won;
  if (isWon && !f.term_sheet_issued_date) missing.push("Term Sheet Issued Date");
  if (isWon && !f.term_sheet_signed_date) missing.push("Term Sheet Signed Date");
  const validationError = missing.length ? "Required: " + missing.join(", ") : null;

  const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;
  const req = (t) => <>{t} <span style={{ color:"#c62828" }}>*</span></>;

  return (
    <Modal wide title={readOnly ? "View Opportunity" : initial ? "Edit Opportunity" : "Add Opportunity"} onClose={closeModal}
      onEdit={readOnly && role.canEdit ? () => { closeModal(); setModal({ type:"opp-edit", rec:initial, accountid, onDone:modal.onDone }); } : null}>

      {initial?.id && (
        <div style={{ display:"flex", gap:3, borderBottom:"3px solid "+B.cyan, marginBottom:16 }}>
          {["details","history"].map(t => (
            <TabBtn key={t} id={t} current={formTab} label={t==="details" ? "Details" : "Notes"} onClick={setFormTab} />
          ))}
        </div>
      )}

      {(!initial?.id || formTab === "details") && <>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <ST title="Opportunity" />
          {lockedAccount
            ? <Field label={req("Account")}>
                {/* A LINK, NOT A LABEL. The account is the one thing on this
                    form you routinely want to go and look at, and reading its
                    name here and then hunting for it in the accounts list is
                    the long way round.
                    It survives ReadOnlyForm because that is a disabled
                    <fieldset>, and a disabled fieldset disables form CONTROLS —
                    input, select, button — not an anchor.
                    The span carries the modal close rather than the anchor:
                    PageLink spreads its extra props AFTER its own onClick, so
                    passing one would replace the navigation instead of adding
                    to it. Closing on the bubble, and only when navClick has
                    called preventDefault, means a ctrl-click that opens a new
                    tab leaves this form where it was. */}
                <div style={{ padding:"7px 0", fontSize:13 }}
                     onClick={e => { if (e.defaultPrevented) closeModal(); }}>
                  <PageLink page="sales-account"
                            params={{ accountid: lockedAccount, companyname: initial?.companyname }}
                            style={{ color:B.cyanDark, fontWeight:600 }}
                            title={"Open " + (initial?.companyname || "this account")}>
                    {initial?.companyname || "This account"}
                  </PageLink>
                </div>
              </Field>
            : <Field label={req("Account")}><FSelect value={f.accountid} onChange={set("accountid")} options={acctOpts} /></Field>}
          <Field label={req("Opportunity Name")}><FInput value={f.name} onChange={set("name")} /></Field>
          <Field label={req("Stage")}><FSelect value={f.stage} onChange={set("stage")} options={stageOpts} /></Field>
          <Field label={req("Opportunity Owner")}><FSelect value={f.owner} onChange={set("owner")} options={withCurrent(userOpts, f.owner, initial?.ownerName)} disabled={!role.isAdmin} /></Field>
          <Field label="Type"><FSelect value={f.opportunity_type} onChange={set("opportunity_type")} options={OPP_TYPE} /></Field>
          <Field label="Collateral Type"><FSelect value={f.collateraltype} onChange={set("collateraltype")} options={COLLATERAL_TYPE} /></Field>
          <Field label="Facility Type"><FSelect value={f.facility_type} onChange={set("facility_type")} options={FACILITY_TYPE} /></Field>
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", gridColumn:"1/-1" }}>
            <input type="checkbox" checked={!!f.follow_up} disabled={readOnly} onChange={e => set("follow_up")(e.target.checked ? 1 : 0)} />
            <span style={{ fontSize:13 }}>Follow Up?</span>
          </div>

          <ST title="Amounts" />
          <Field label="RLOC Amount"><FInputNum value={f.rloc_amount} onChange={set("rloc_amount")} /></Field>
          <Field label="Term Amount"><FInputNum value={f.term_amount} onChange={set("term_amount")} /></Field>
          <Field label="Commitment Amount"><div style={{ padding:"7px 0", fontSize:13, fontWeight:600 }}>{fmt(commitment)}</div></Field>
          <Field label="Initial Funding Amount"><FInputNum value={f.initial_funding_amount} onChange={set("initial_funding_amount")} /></Field>
          <Field label="Expected Equity Requirement"><FInputNum value={f.expected_equity_requirement} onChange={set("expected_equity_requirement")} /></Field>
          <Field label="Probability (%)"><FInputNum value={f.probability} onChange={set("probability")} /></Field>

          <ST title="Dates" />
          <Field label={req("Expected Close Date")}><FInput value={d(f.expected_close_date)} onChange={set("expected_close_date")} type="date" /></Field>
          <Field label={isWon ? req("Term Sheet Issued Date") : "Term Sheet Issued Date"}><FInput value={d(f.term_sheet_issued_date)} onChange={set("term_sheet_issued_date")} type="date" /></Field>
          <Field label={isWon ? req("Term Sheet Signed Date") : "Term Sheet Signed Date"}><FInput value={d(f.term_sheet_signed_date)} onChange={set("term_sheet_signed_date")} type="date" /></Field>
          <Field label="HUM Date"><FInput value={d(f.hum_date)} onChange={set("hum_date")} type="date" /></Field>
          <Field label="IRC Date"><FInput value={d(f.irc_date)} onChange={set("irc_date")} type="date" /></Field>
          {!readOnly && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginBottom:4 }}>
            Term Sheet Issued fills automatically at the Term Sheet stage; Term Sheet Signed fills at Due Diligence (only if blank). Both are required before Funded (Won).
          </div>}

          <ST title="Termination" />
          <Field label={f.stage === "Declined (Lost)" ? req("Termination Reason") : "Termination Reason"}><FSelect value={f.termination_reason} onChange={set("termination_reason")} options={TERMINATION_REASON} /></Field>
          <Field label="Terminated Date"><FInput value={d(f.terminated_date)} onChange={set("terminated_date")} type="date" disabled /></Field>
          {!readOnly && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginBottom:4 }}>Terminated Date is set automatically when the stage becomes Declined (Lost).</div>}

          <ST title="Source" />
          <Field label={req("Lead Source")}><FSelect value={f.lead_source} onChange={set("lead_source")} options={f.lead_source && !LEAD_SOURCE.some(o => o.value === f.lead_source) ? [...LEAD_SOURCE, { value: f.lead_source, label: f.lead_source + " (from lead)" }] : LEAD_SOURCE} /></Field>
          <Field label={req("Source Firm")}><FInput value={f.source_firm} onChange={set("source_firm")} /></Field>
          <Field label="Referral Source"><FSelect value={f.referralsourceid} onChange={set("referralsourceid")} options={refOpts} /></Field>
          <div />

          <ST title="Next Step & Description" />
          <div style={{ gridColumn:"1/-1" }}>
            <Field label="Next Step"><FInput value={f.next_step || ""} onChange={set("next_step")} /></Field>
            {!readOnly && <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginTop:2, marginBottom:6 }}>Changing Next Step archives the previous value to the Notes tab.</div>}
            <Field label="Description"><FInput value={f.description || ""} onChange={set("description")} /></Field>
          </div>
        </div>
      </ReadOnlyForm>

      {!readOnly && (
        <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginTop:10 }}>
          Moving to Due Diligence or later makes this account visible to portfolio managers; Funded (Won) moves it to Client.
        </div>
      )}
      </>}

      {initial?.id && formTab === "history" && (
        <div style={{ padding:"2px" }}>
          <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginBottom:10 }}>
            Notes on this opportunity roll up to its account. Next Step changes are also archived here automatically.
          </div>
          <NotesPanel entitytype="Opportunity" entityid={initial.id} accountid={initial.accountid} />
        </div>
      )}

      {!readOnly && formTab === "details" && validationError && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>{validationError}</div>}
      {!readOnly && error && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{error}</div>}

      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:12 }}>
        <Btn onClick={closeModal}>{readOnly ? "Close" : "Cancel"}</Btn>
        {!readOnly && <Btn disabled={saving || !!validationError}
          onClick={() => saveRec("saveOpportunity", { ...f, accountid: f.accountid || accountid }, initial ? "Edited" : "Added", "Opportunities", (initial ? "Updated" : "Added") + " opportunity", modal.onDone)}
          variant="primary">{saving ? "Saving..." : "Save"}</Btn>}
      </div>
    </Modal>
  );
};
